// Brief Sep 2026 — Onglet « Revenus » de Mes sessions : KPIs (mois + total),
// prochaines sessions payantes, historique des réservations payées, statut
// Stripe. Remplace le mini-widget revenus (iter_80) qui vivait en haut de page.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Banknote, Hourglass, TrendingUp, Calendar, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { parseUtc } from '../lib/calendarDate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDate = (iso) => {
  const d = parseUtc(iso);
  return d ? d.toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
};

const STATUS_LABELS = {
  confirmed: { label: 'Payé', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  completed: { label: 'Payé', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  pending_payment: { label: 'En attente', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
};

const KpiCard = ({ testId, icon: Icon, label, value, sub, tone }) => (
  <div
    data-testid={testId}
    className="flex-1 min-w-[180px] p-4 rounded-2xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm"
  >
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
      <Icon className={`h-4 w-4 ${tone}`} /> {label}
    </div>
    <p className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
);

export const RevenueTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/me/revenue-details`, { withCredentials: true })
      .then(({ data: d }) => setData(d))
      .catch(() => toast.error('Impossible de charger les revenus.'))
      .finally(() => setLoading(false));
  }, []);

  const connectStripe = async () => {
    try {
      const { data: r } = await axios.post(`${API}/stripe/connect/onboard`, {}, { withCredentials: true });
      if (r?.url) window.location.href = r.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Onboarding Stripe indisponible.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="revenue-tab-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#7F77DD]" />
      </div>
    );
  }
  if (!data) return null;

  const upcoming = data.upcoming_paid_sessions || [];
  const recent = data.recent_paid_bookings || [];

  return (
    <div className="space-y-6" data-testid="revenue-tab">
      {/* Statut Stripe */}
      {data.stripe_charges_enabled ? (
        <div
          data-testid="revenue-stripe-status"
          className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-sm text-emerald-700 dark:text-emerald-300"
        >
          <CheckCircle2 className="h-4 w-4" />
          Stripe connecté — les paiements te sont versés directement, 100% du montant (0% de commission).
        </div>
      ) : (
        <div
          data-testid="revenue-stripe-status"
          className="flex flex-wrap items-center gap-3 px-4 py-3 rounded-2xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800"
        >
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
          <p className="text-sm text-amber-800 dark:text-amber-200 flex-1 min-w-[220px]">
            Stripe n'est pas encore connecté : tes sessions payantes ne sont pas réservables.
            100% du montant te revient — aucune commission OpenSapiens.
          </p>
          <button
            type="button"
            onClick={connectStripe}
            data-testid="revenue-connect-stripe-btn"
            className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-xs font-bold transition-colors"
          >
            Connecter Stripe
          </button>
        </div>
      )}

      {/* KPIs */}
      <div className="flex flex-wrap gap-3">
        <KpiCard
          testId="revenue-kpi-month-earned"
          icon={Banknote}
          tone="text-emerald-600"
          label={`Encaissé — ${data.month_label}`}
          value={`${data.month_earned_chf} CHF`}
          sub={`${data.month_earned_count} réservation${data.month_earned_count > 1 ? 's' : ''} payée${data.month_earned_count > 1 ? 's' : ''}`}
        />
        <KpiCard
          testId="revenue-kpi-month-pending"
          icon={Hourglass}
          tone="text-amber-600"
          label="En attente ce mois"
          value={`${data.month_pending_chf} CHF`}
          sub={`${data.month_pending_count} paiement${data.month_pending_count > 1 ? 's' : ''} en attente`}
        />
        <KpiCard
          testId="revenue-kpi-total"
          icon={TrendingUp}
          tone="text-[#7F77DD]"
          label="Total depuis le début"
          value={`${data.total_earned_chf} CHF`}
          sub={`${data.total_earned_count} réservation${data.total_earned_count > 1 ? 's' : ''} payée${data.total_earned_count > 1 ? 's' : ''} au total`}
        />
      </div>

      {/* Prochaines sessions payantes */}
      <div className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 overflow-hidden" data-testid="revenue-upcoming-table">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
          <Calendar className="h-4 w-4 text-[#7F77DD]" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Prochaines sessions payantes</h3>
        </div>
        {upcoming.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-400" data-testid="revenue-upcoming-empty">
            Aucune session payante à venir. Crée une session avec un prix depuis l'onglet Sessions.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-100 dark:border-gray-700">
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Session</th>
                <th className="px-4 py-2 text-right">Prix</th>
                <th className="px-4 py-2 text-right hidden sm:table-cell">Inscrits</th>
                <th className="px-4 py-2 text-right">Revenu engagé</th>
              </tr>
            </thead>
            <tbody>
              {upcoming.map((ev) => (
                <tr key={ev.event_id} className="border-b border-gray-50 dark:border-gray-700/40 last:border-0" data-testid={`revenue-upcoming-row-${ev.event_id}`}>
                  <td className="px-4 py-2.5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{fmtDate(ev.start_time)}</td>
                  <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-white">{ev.title}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">{ev.price} {ev.currency || 'CHF'}</td>
                  <td className="px-4 py-2.5 text-right hidden sm:table-cell text-gray-600 dark:text-gray-300">
                    {ev.booked_count}{ev.max_capacity ? ` / ${ev.max_capacity}` : ''}
                    {ev.pending_count > 0 && <span className="text-amber-600 text-xs"> (+{ev.pending_count} en attente)</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right font-semibold text-emerald-700 dark:text-emerald-300 whitespace-nowrap">
                    {ev.booked_chf} CHF
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Historique des réservations payées */}
      <div className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 overflow-hidden" data-testid="revenue-history-list">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
          <Banknote className="h-4 w-4 text-emerald-600" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Dernières réservations payées</h3>
        </div>
        {recent.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-400" data-testid="revenue-history-empty">
            Aucune réservation payante pour l'instant.
          </p>
        ) : (
          <ul>
            {recent.map((b) => {
              const st = STATUS_LABELS[b.status] || { label: b.status, cls: 'bg-gray-100 text-gray-600' };
              return (
                <li key={b.booking_id} className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-50 dark:border-gray-700/40 last:border-0" data-testid={`revenue-history-row-${b.booking_id}`}>
                  <Avatar className="h-8 w-8 shrink-0">
                    <AvatarImage src={b.participant_avatar} />
                    <AvatarFallback className="bg-gradient-to-br from-[#7F77DD] to-cyan-500 text-white text-xs">
                      {(b.participant_name || '?').substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{b.participant_name}</p>
                    <p className="text-xs text-gray-400 truncate">{b.title} · {fmtDate(b.start_time)}</p>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                  <span className="text-sm font-bold text-gray-900 dark:text-white whitespace-nowrap">{b.amount} {b.currency}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default RevenueTab;
